import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";
import CheckboxBoolean from "components/app/function/CheckboxBoolean";
import DatePicker from "components/app/function/DatePicker";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { X } from "lucide-react";

import { useEffect, useState } from "react";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";

interface DialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean) => void;
  editEducation: any;
  setEditEducation: (value: any) => void;
  setUserProfileDetail: (value: any) => void;
}

export default function DailogEducations({ editEducation = null, isDialogOpen = false, setIsDialogOpen, setEditEducation, setUserProfileDetail }: DialogProps) {
  const [isPresent, setIsPresent] = useState<boolean>(true);
  const [isGraduated, setIsGraduated] = useState<boolean>(false);
  const { token } = useAppSelector((state) => state.auth);

  const FormSchema = z.object({
    school_name: z.string().min(1, { message: "School Name is required*" }),
    concentration: z.string().min(1, { message: "Concentration is required*" }),
    starting_date: z.string().min(1, { message: "Starting date is required*" }),
    ending_date: z.string().optional(), // Optional to handle isPresent logic
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      school_name: "",
      concentration: "",
      starting_date: "",
      ending_date: "",
    },
    resolver: zodResolver(FormSchema),
  });

  // Sync state when dialog opens or editEducation changes
  useEffect(() => {
    if (isDialogOpen && editEducation) {
      reset({
        school_name: editEducation.school_name || "",
        concentration: editEducation.concentration || "",
        starting_date: editEducation.starting_date || "",
        ending_date: editEducation.ending_date || "",
      });
    } else if (!isDialogOpen) {
      reset({
        school_name: "",
        concentration: "",
        starting_date: "",
        ending_date: "",
      });
    }
  }, [isDialogOpen, editEducation, reset]);

  const submitProfileApi = async (data: z.infer<typeof FormSchema>) => {
    try {
      const url = `${RouteApi.submitUpdateUserEducation}token=${token}`;
      const submitData = {
        user_education_id: editEducation ? editEducation.id : "0",
        school_name: data.school_name,
        concentration: data.concentration,
        starting_date: data.starting_date,
        ending_date: data.ending_date,
        is_present: isPresent,
        is_graduated: isGraduated,
      };

      const response = await RequestApi(url, { data: submitData });

      if (response.status === "success") {
        setEditEducation(response.user_educations);
        setUserProfileDetail((prev: any) => ({
          ...prev,
          user_educations: response.user_educations.sort((a: any, b: any) => b.id - a.id),
        }));
      }
    } catch (error) {
      AppLog.show({
        message: `[submitProfileApi]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setIsDialogOpen(false);
    }
  };

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent isShowBtnXClose={false}>
        <DialogHeader>
          <DialogTitle>{editEducation ? "Edit Education" : "Add Education"}</DialogTitle>
          <DialogDescription>Make changes to your profile here. Click save when you're done.</DialogDescription>
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>
        <form className="space-y-2" onSubmit={handleSubmit(submitProfileApi)}>
          <div className="mt-1">
            <h5 className="text-sm font-semibold">School Name</h5>
            <Controller name="school_name" control={control} render={({ field }) => <Input type="text" className="mt-1" value={field.value || ""} onChange={field.onChange} />} />
            {errors.school_name && <p className="text-red-600 text-xs">{errors.school_name.message}</p>}
          </div>

          <div className="mt-1">
            <h5 className="text-sm font-semibold">Concentration</h5>
            <Controller name="concentration" control={control} render={({ field }) => <Input type="text" className="mt-1" value={field.value || ""} onChange={field.onChange} />} />
            {errors.concentration && <p className="text-red-600 text-xs">{errors.concentration.message}</p>}
          </div>

          <div className="mt-1">
            <h5 className="text-sm font-semibold">Starting Date</h5>
            <Controller
              name="starting_date"
              control={control}
              render={({ field }) => <DatePicker className="mt-1" value={field.value || ""} onChange={(value) => field.onChange(value)} />}
            />
            {errors.starting_date && <p className="text-red-600 text-xs">{errors.starting_date.message}</p>}
          </div>

          <div className="mt-1">
            {!isPresent && (
              <>
                <h5 className="text-sm font-semibold">Ending Date</h5>
                <Controller
                  name="ending_date"
                  control={control}
                  render={({ field }) => <DatePicker value={field.value || ""} className="mt-1" onChange={(value) => field.onChange(value)} />}
                />
              </>
            )}
          </div>

          <CheckboxBoolean
            className="mt-2"
            value={isPresent}
            id={`isPresent`}
            label="Present"
            onValueChange={(value) => setIsPresent(value)} // Properly handle updates
          />

          <CheckboxBoolean
            className="mt-2"
            value={isGraduated}
            id={`isGraduated`}
            label="Graduated"
            onValueChange={(value) => setIsGraduated(value)} // Properly handle updates
          />

          <DialogFooter>
            <Button className="bg-destructive text-white bg-blue-600 hover:bg-blue-500">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
